"use client";

import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import BrochureModal from "./BrochureModal";
import { Card, CardContent } from "@/components/ui/card";
import Footer from "./Footer";
import ERPLeadBot from "./ERPLeadBot";
import {
  Bus,
  Package,
  GraduationCap,
  CheckCircle2,
  ArrowRight,
  MapPin,
  BarChart3,
  Bell,
  ShieldCheck,
  Smartphone,
  Clock,
} from "lucide-react";

const products = [
  {
    icon: GraduationCap,
    title: "School Management System",
    color: "blue",
    description:
      "Complete ERP for schools, fees, attendance, timetable, payroll, grades, and real-time parent communication in one platform.",
    features: [
      "Fee collection & automated reminders",
      "Digital attendance with notifications",
      "Smart timetable generation",
      "Grade management & report cards",
    ],
  },
  {
    icon: Bus,
    title: "Bus Management System",
    color: "green",
    description:
      "Real-time GPS tracking for school and corporate fleets. Know where every bus is, always, and keep parents informed automatically.",
    features: [
      "Live GPS tracking on map",
      "Route & stop management",
      "Driver & vehicle records",
      "Parent mobile app alerts",
    ],
  },
  {
    icon: Package,
    title: "Inventory Management System",
    color: "purple",
    description:
      "Take full control of your stock. Track items, manage suppliers, and get low-stock alerts before you run out.",
    features: [
      "Real-time stock tracking",
      "Purchase & supplier management",
      "Low-stock alerts & reports",
      "Multi-location inventory",
    ],
  },
];

const platformFeatures = [
  {
    icon: BarChart3,
    title: "Real-Time Analytics",
    description: "Live dashboards and reports so you always know what's happening across your operations.",
  },
  {
    icon: Bell,
    title: "Automated Notifications",
    description: "SMS, email, and in-app alerts for the right people at the right time, no manual follow-up needed.",
  },
  {
    icon: ShieldCheck,
    title: "Role-Based Access",
    description: "Every team member sees only what they need. Secure, clean, and easy to manage.",
  },
  {
    icon: Smartphone,
    title: "Mobile Ready",
    description: "Access from any device. Android and iOS apps available for on-the-go management.",
  },
  {
    icon: Clock,
    title: "Saves Hours Daily",
    description: "Automate repetitive tasks and reduce manual work by 20+ hours per week.",
  },
  {
    icon: MapPin,
    title: "Cloud-Based",
    description: "No installations. Access your data securely from anywhere, anytime.",
  },
];

const colorMap = {
  blue: {
    bg: "bg-blue-50",
    icon: "bg-blue-100 text-blue-600",
    border: "hover:border-blue-200",
    badge: "bg-blue-600",
  },
  green: {
    bg: "bg-green-50",
    icon: "bg-green-100 text-green-600",
    border: "hover:border-green-200",
    badge: "bg-green-600",
  },
  purple: {
    bg: "bg-purple-50",
    icon: "bg-purple-100 text-purple-600",
    border: "hover:border-purple-200",
    badge: "bg-purple-600",
  },
};

export default function Home() {
  const [showBrochure, setShowBrochure] = useState(false);

  return (
    <div className="w-full">
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-indigo-50 overflow-hidden">
        {/* Decorative background blobs */}
        <div className="absolute right-0 top-0 w-[700px] h-[700px] bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full opacity-50 translate-x-1/3 -translate-y-1/3 pointer-events-none blur-3xl" />
        <div className="absolute right-32 top-24 w-[450px] h-[450px] bg-blue-200 rounded-full opacity-20 pointer-events-none blur-2xl" />
        <div className="absolute left-0 bottom-0 w-[300px] h-[300px] bg-indigo-100 rounded-full opacity-30 -translate-x-1/2 translate-y-1/2 pointer-events-none blur-2xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-0">
          <div className="grid lg:grid-cols-2 gap-12 items-center">

            {/* Left — Text */}
            <div className="pb-14">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 text-blue-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-7 shadow-sm">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-500 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-600" />
                </span>
                One Platform. Three Powerful Products.
              </div>

              {/* Headline */}
              <h1 className="text-5xl lg:text-[3.75rem] font-black text-gray-900 leading-[1.1] mb-6 tracking-tight">
                Smart Management Software{" "}
                <span className="relative inline-block text-blue-600">
                  Built for India
                  <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 300 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2 8.5C50 3 100 1 150 3.5C200 6 250 9 298 5.5" stroke="#2563eb" strokeWidth="3" strokeLinecap="round"/>
                  </svg>
                </span>
              </h1>

              {/* Description */}
              <p className="text-lg text-gray-500 mb-8 max-w-lg leading-relaxed">
                Clynox helps schools, fleet operators, and businesses automate daily operations, saving time, reducing errors, and keeping everyone informed.
              </p>

              {/* Product cards */}
              <div className="grid grid-cols-3 gap-3 mb-8">
                {[
                  { icon: GraduationCap, label: "School\nManagement",    to: "/products/school-management",    iconBg: "bg-blue-100",   iconColor: "text-blue-600",   border: "hover:border-blue-300 hover:bg-blue-50/50" },
                  { icon: Bus,           label: "Bus\nManagement",       to: "/products/bus-management",       iconBg: "bg-green-100",  iconColor: "text-green-600",  border: "hover:border-green-300 hover:bg-green-50/50" },
                  { icon: Package,       label: "Inventory\nManagement", to: "/products/inventory-management", iconBg: "bg-purple-100", iconColor: "text-purple-600", border: "hover:border-purple-300 hover:bg-purple-50/50" },
                ].map(({ icon: Icon, label, to, iconBg, iconColor, border }) => (
                  <Link
                    key={to}
                    to={to}
                    className={`group flex flex-col gap-2.5 p-4 rounded-2xl border border-gray-200 bg-white shadow-sm transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 ${border}`}
                  >
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${iconBg}`}>
                      <Icon className={`w-5 h-5 ${iconColor}`} />
                    </div>
                    <span className="text-xs font-semibold text-gray-800 leading-tight whitespace-pre-line">{label}</span>
                    <ArrowRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500 group-hover:translate-x-0.5 transition-all" />
                  </Link>
                ))}
              </div>

              {/* CTAs */}
              <div className="flex flex-wrap gap-3 items-center">
                <Link to="/contact">
                  <Button size="lg" className="bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-200 gap-2 px-6">
                    Start Free Trial <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Button
                  size="lg"
                  variant="outline"
                  className="gap-2 border-gray-300 hover:border-gray-400"
                  onClick={() => setShowBrochure(true)}
                >
                  Download Brochure
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                </Button>
                {showBrochure && <BrochureModal onClose={() => setShowBrochure(false)} />}
              </div>
            </div>

            {/* Right — Dashboard mockup */}
            <div className="relative hidden lg:flex items-end pb-0">
              {/* Glow behind the card */}
              <div className="absolute inset-x-4 bottom-0 h-3/4 bg-blue-400/10 rounded-3xl blur-2xl pointer-events-none" />

              {/* Browser frame */}
              <div className="relative w-full rounded-2xl shadow-[0_20px_60px_-10px_rgba(37,99,235,0.25)] overflow-hidden border border-gray-200/80 bg-white ring-1 ring-blue-100">
                {/* Browser chrome */}
                <div className="bg-gray-50 px-4 py-2.5 flex items-center gap-3 border-b border-gray-200">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                    <div className="w-3 h-3 rounded-full bg-green-400" />
                  </div>
                  <div className="flex-1 mx-2 bg-white rounded-md px-3 py-1 text-xs text-gray-400 border border-gray-200 flex items-center gap-1.5">
                    <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 11c0-1.1.9-2 2-2s2 .9 2 2-2 4-2 4m-2-4c0-1.1-.9-2-2-2s-2 .9-2 2 2 4 2 4m4-4h.01"/><circle cx="12" cy="12" r="10"/></svg>
                    clynox.com
                  </div>
                </div>
                <img
                  src="/Dashboards.png"
                  alt="Clynox Dashboard"
                  className="w-full object-cover"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Trust strip */}
        <div className="relative mt-0 border-t border-gray-100 bg-white/80 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-7">
            <div className="flex items-center justify-center gap-12">
              {[
                { value: "500+", label: "Client Trust Us" },
                { value: "50K+", label: "Active Users" },
                { value: "98%",  label: "Satisfaction Rate" },
              ].map((stat, i, arr) => (
                <div key={stat.value} className="flex items-center gap-12">
                  <div className="text-center">
                    <div className="text-2xl font-extrabold text-gray-900">{stat.value}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{stat.label}</div>
                  </div>
                  {i < arr.length - 1 && <div className="w-px h-8 bg-gray-200" />}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Products */}
      <section id="products" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Products</h2>
            <p className="text-xl text-gray-500 max-w-xl mx-auto">
              Purpose-built solutions for different operational needs, all under one roof.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 items-stretch">
            {products.map((product, index) => {
              const c = colorMap[product.color];
              return (
                <Card
                  key={index}
                  className={`border-2 ${c.border} transition-all duration-200 hover:shadow-md flex flex-col overflow-hidden`}
                >
                  <div className={`h-1.5 w-full ${c.badge}`} />
                  <CardContent className="p-7 flex flex-col flex-1">
                    <div className={`w-12 h-12 ${c.icon} rounded-xl flex items-center justify-center mb-5`}>
                      <product.icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{product.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed mb-5">{product.description}</p>
                    <ul className="space-y-2 mb-6 flex-1">
                      {product.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                          <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                          {f}
                        </li>
                      ))}
                    </ul>
                    <Link to="/contact" className="mt-auto">
                      <Button variant="outline" size="sm" className="w-full group">
                        Get a Demo
                        <ArrowRight className="w-3.5 h-3.5 ml-2 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Platform Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Everything on One Platform
            </h2>
            <p className="text-xl text-gray-500 max-w-xl mx-auto">
              Core capabilities shared across all Clynox products, so your team spends less time on admin.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {platformFeatures.map((feature, index) => (
              <Card key={index} className="border hover:border-blue-200 transition-colors bg-white">
                <CardContent className="p-6">
                  <div className="w-11 h-11 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                    <feature.icon className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Clynox */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
              <img
                src="/Dashboards.png"
                alt="Clynox dashboard"
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Why Businesses Choose Clynox
              </h2>
              <p className="text-lg text-gray-500 mb-8">
                Hundreds of schools, fleet operators, and businesses across India use Clynox to run their operations, because it just works.
              </p>
              <div className="space-y-4">
                {[
                  "Save 20+ hours per week on manual tasks",
                  "Improve communication with stakeholders by 85%",
                  "Reduce paperwork and human errors",
                  "Get real-time visibility across operations",
                  "Affordable pricing, built for Indian budgets",
                  "Access from anywhere, web & mobile apps",
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
              <Link to="/contact">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 mt-8">
                  Get Started Today
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-800">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Simplify Your Operations?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Start your free 30-day trial. No credit card required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
                Start Free Trial
              </Button>
            </Link>
            <Link to="/contact">
              <Button
                size="lg"
                variant="outline"
                className="border-white bg-transparent text-white hover:bg-white/10 hover:text-white"
              >
                Contact Sales
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <ERPLeadBot />
    </div>
  );
}
